import { Inject } from '@odinjs/odin';
import { ContextualBarAPI, FormAPI, InputAPI, MediaContainerAPI, MediaContainerData, PanelAPI, PDFViewerAPI, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService, ModelService, } from '@philips-internal/oswald-ui-core/lib/services';

import { Exam } from '../../../interfaces/exam';
import { ReportStatus } from './../../../constants/enums/report-status';
import ReportService from '../../../services/report-service';
import BaseController from '../../BaseController';
import { BrowserService } from '../../commons/browser/browser-service';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { Endpoint } from './../../../constants/endpoints';

import './lab-exams-details-controller.css';
import { DeckContext } from '../../commons/Deck/deck-service';
import { FragmentNames, LabExamsDetailsAliases } from '../../../codegen';
import ShowToast from '../../../utils/toast-service';

interface ReferenceStatus {
  className: string;
  statusExpression: string;
}
@Controller({ fragment: FragmentNames.LAB_EXAMS_DETAILS })
class LabExamsDetails extends BaseController {

  @Inject(LabExamsDetailsAliases.FORM_LAB_EXAMS_DETAILS)
  private formAPI!: FormAPI;

  @Inject(LabExamsDetailsAliases.CONTEXTUAL_BAR_LAB_EXAMS)
  private contextualBarApi!: ContextualBarAPI;

  @Inject(LabExamsDetailsAliases.INPUT_REFERENCE)
  private inputReferenceApi!: InputAPI;

  @Inject(LabExamsDetailsAliases.TEXT_DSRESULT)
  private textResultApi!: TextAPI;

  @Inject(LabExamsDetailsAliases.TEXT_DSSTATUS)
  private textStatusApi!: TextAPI;

  @Inject(LabExamsDetailsAliases.PDF_VIEWER_EXAM_DETAILS)
  private pdfViewerApi!: PDFViewerAPI;

  @Inject(LabExamsDetailsAliases.PANEL_MEDIA_CONTAINER_PDF_VIEWER)
  private panelMediaContainerPDFViewerApi!: PanelAPI;

  @Inject(LabExamsDetailsAliases.MEDIA_CONTAINER_NON_LAB)
  private mediaContainerApi!: MediaContainerAPI;

  @Inject(LabExamsDetailsAliases.PANEL_LOADER)
  private panelLoader!: PanelAPI;

  @Inject(LabExamsDetailsAliases.PANEL_PDF_VIEWER_EXAM_DETAILS)
  private panelPdfViewerExamDetailsAPI!: PanelAPI;

  @OnMount(LabExamsDetailsAliases.FORM_LAB_EXAMS_DETAILS)
  handleMountExamsDetails(): void {

    InformationBarUtils.collapsedBar = true;

    const { patientId, sequenceId } = this.historyService.getCurrentRouteParams();
    const prescriptionId = this.getHistoryRouteParam<number>('prescriptionId');
    const resultId = this.getHistoryRouteParam<number>('resultId');
    const itens = DeckContext.getParamActivate();
    const exams = itens?.find(item => item.nrSequencia === sequenceId && item.nrPrescricao === prescriptionId);
    const encounterId = exams ? exams.nrAtendimento : this.historyService.getCurrentRouteParams().encounterId;
    DeckContext.clearParamActivate();
    const params = {
      browser: BrowserService.detectBrowser(),
      encounterId,
      patientId,
      sequenceId,
    }

    this.context.set(params);

    if (prescriptionId) {
      this.loadExam({ ...params, prescriptionId } as Record<string, Value>, Endpoint.GET_LAB_EXAMS_BY_PRESCRIPTION);
    } else if (resultId) {
      this.loadExam({ ...params, resultId } as Record<string, Value>, Endpoint.GET_LAB_EXAMS_BY_RESULT);
    }
  }

  @OnMount(LabExamsDetailsAliases.PDF_VIEWER_EXAM_DETAILS)
  handleMountPdfViewer(): void {
    const file = this.context.get('reportFile')

    this.pdfViewerApi.setDownloadPdfExpression('tws.view-report');
    this.pdfViewerApi.setOpenPdfExpression('text.open-report');
    if (!file) {
      this.panelPdfViewerExamDetailsAPI.setVisible(false);
      return;
    }
    this.panelLoader.setVisible(false)
    this.pdfViewerApi.setVisible(true);
    this.pdfViewerApi.setSrc(file);
  }

  @OnMount(LabExamsDetailsAliases.MEDIA_CONTAINER_NON_LAB)
  handleMountMediaContainerNonLab(): void {
    const mediaContainerData = this.context.get('mediaContainerData') as MediaContainerData[]
    if (!mediaContainerData) {
      this.panelMediaContainerPDFViewerApi.setVisible(false);
      return;
    }
    this.panelLoader.setVisible(false)
    this.mediaContainerApi.setData(mediaContainerData);
  }

  @OnClick(LabExamsDetailsAliases.IMAGE_VIEWER_BUTTON)
  handleClickImageViewerButton(): void {
    this.mediaContainerApi.open();
  }

  loadExam(params: Record<string, Value>, endPoint: string): void {
    this.formAPI.setLoading(true)

    HttpService.get(endPoint, { ...params }).then(response => {
      const exam = response.body as Exam;
      this.fillFields(exam);
      this.handleReportLoading(exam);
    }).catch((error) => {
      ShowToast.open(error?.body.errorMessage, ToastLevel.ERROR);
    }).finally(() => {
      this.formAPI.setLoading(false)
    });
  }

  handleReportLoading(exam: Exam): void {
    if (exam.reportAvailable === ReportStatus.REPORT_NOT_AVAILABLE) {
      this.panelPdfViewerExamDetailsAPI.setVisible(false);
      this.panelMediaContainerPDFViewerApi.setVisible(false);
      this.panelLoader.setVisible(false);
      return;
    }

    this.loadReport(exam);
  }

  loadReport(dataInformation: Exam): void {
    const params = this.createReportData(dataInformation);
    const reportService = new ReportService(this);

    reportService.generateReport(params).then(reportFile => {
      this.context.set({ reportFile });
    }).catch(() => {
      this.panelPdfViewerExamDetailsAPI.setVisible(false);
      this.errorReport();
    }).finally(() => {
      this.panelLoader.setVisible(false)
      this.panelPdfViewerExamDetailsAPI.setVisible(true)
    });
  }

  createReportData(dataInformation: Exam): Record<string, Value> {
    return {
      CD_ESTABELECIMENTO: dataInformation.cdEstabelecimento || '1',
      EXAM_NAME: dataInformation.dsExame,
      EXAM_TYPE: 'L',
      IE_FORMATO: dataInformation.ieFormatoTexto || 0,
      NR_ACESSO_DICOM: 0,
      NR_PRESCRICAO: dataInformation.nrPrescricao || '',
      NR_SEQ_RESULTADO: dataInformation.nrSeqResultado,
      NR_SEQUENCIA_P: dataInformation.nrSequencia,
    }
  }

  getColorResultValue(result: string | undefined, minRef: number | undefined, maxRef: number | undefined): ReferenceStatus {

    let res;

    if (result) {

      result = result.replace(',', '.');
      res = parseFloat(result);

    }

    const colorClass = {
      blue: {
        className: 'c-text-detail__value_result-blue'
      },
      default: {
        className: 'c-text-detail__value_result'
      },
      red: {
        className: 'c-text-detail__value_result-red'
      },
      yellow: {
        className: 'c-text-detail__value_result-yellow'
      },
    }

    let className = colorClass.default.className;
    let statusExpression = '';

    if (minRef == null || maxRef == null) {
      className = colorClass.default.className;

    } else {

      if (res != null) {

        if (res >= minRef && res <= maxRef) {

          className = colorClass.blue.className;
          statusExpression = 'tws.label-between-value';

        } else {

          if (res > maxRef) {
            className = colorClass.red.className;
            statusExpression = 'tws.label-max-value';

          } else if (res < minRef) {
            className = colorClass.yellow.className;
            statusExpression = 'tws.label.min-value';
          }

        }

      }

    }

    const referenceStatus: ReferenceStatus = {
      className: className,
      statusExpression: statusExpression
    }

    return referenceStatus;

  }

  fillFields(exam: Exam): void {
    const record = { ...this.formAPI.getData(), ...exam };

    let minRef;
    let maxRef;

    let dsValorRefMin = exam.dsValorRefMin;
    let dsValorRefMax = exam.dsValorRefMax;
    let examDesc = exam.dsExame;
    const measureUnit = exam.dsUnidMedida;
    let result = exam.dsResultado;

    if (dsValorRefMin) {
      dsValorRefMin = dsValorRefMin.replace(',', '.');
      minRef = parseFloat(dsValorRefMin);
    }

    if (dsValorRefMax) {
      dsValorRefMax = dsValorRefMax.replace(',', '.');
      maxRef = parseFloat(dsValorRefMax);
    }

    const referenceStatus = this.getColorResultValue(result, minRef, maxRef);
    this.textResultApi.setClassName(referenceStatus.className);
    this.formAPI.setData(record);

    if (measureUnit) {
      examDesc = `${examDesc}(${measureUnit})`;
    }

    let formattedResult = result;
    const formattedMinRef = ModelService.format('numeric', minRef, {});
    const formattedMaxRef = ModelService.format('numeric', maxRef, {});

    if (result) {
      result = result.replace(',', '.');
      const res = parseFloat(result);

      if (!isNaN(res)) {
        formattedResult = ModelService.format('numeric', res, {});
      }
    }

    if (examDesc) {
      this.contextualBarApi.setTitle(examDesc);
    }

    if (formattedResult) {
      this.textResultApi.setValue(formattedResult)
    }

    this.inputReferenceApi.setValue(`${formattedMinRef} - ${formattedMaxRef}`);
    this.textStatusApi.setValue(referenceStatus.statusExpression);

  }

  errorReport(): void {
    ToastService.open({
      dismissible: true,
      highlight: true,
      id: 'prescription-report-toast',
      level: ToastLevel.ERROR,
      message: MessageService.translate('tws.error-generate-report'),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      title: MessageService.translate('tws.error-generic'),
      type: ToastType.BANNER,
    });
  }
}

export default LabExamsDetails;
